import { useCallback, useState } from "react";
import { Plus, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { searchDrivers } from "@/lib/api";
import { DriverList } from "@/components/drivers/DriverList";
import { DriverRecordSheet } from "@/components/drivers/DriverRecordSheet";
import { AddDriverDialog } from "@/components/drivers/AddDriverDialog";

const STAFF = ["client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"];

/** Site staff: the tenant's drivers; open one to manage which carriers they drive for. */
export default function AdminDrivers() {
  const { assignments = [], hasRole } = useAuth();
  const { viewAsClientId } = useViewAsClient() || {};
  const clientId = hasRole(["super_admin"])
    ? viewAsClientId
    : assignments.find((a) => STAFF.includes(a.role) && a.client_id)?.client_id;
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(
    (q) => (clientId ? searchDrivers({ q, clientId, limit: 100 }) : Promise.resolve({ data: [] })),
    [clientId],
  );

  return (
    <div className="space-y-6" data-testid="admin-drivers-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Truck className="h-6 w-6" /> Drivers</h1>
          <p className="text-sm text-slate-500 mt-1">Drivers who work at your sites. Open a driver to choose which carriers they drive for.</p>
        </div>
        <Button onClick={() => setAdding(true)} disabled={!clientId} data-testid="admin-drivers-add-btn"><Plus className="h-4 w-4 mr-1" /> Add driver</Button>
      </div>
      {!clientId ? (
        <p className="text-sm text-slate-500" data-testid="admin-drivers-pick-client">Pick a client with &ldquo;View as&rdquo; to see its drivers.</p>
      ) : (
        <DriverList load={load} reloadKey={reloadKey} onSelect={setOpen} testid="admin-drivers"
                    empty="No drivers yet. Drivers appear here once they're added or assigned to a booking." />
      )}
      <DriverRecordSheet driver={open} clientId={clientId} onOpenChange={(v) => !v && setOpen(null)} />
      <AddDriverDialog open={adding} onOpenChange={setAdding} clientId={clientId} onAdded={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}
